/**
 * Registering and unregistering this browser for push.
 *
 * Tokens live in `userDevices/{uid}`, one entry per device, rather than in a
 * single field on the public profile. Two reasons: the profile is readable by
 * every signed-in player, so a token there was handed to everyone; and one
 * field meant one device, so enabling notifications on a laptop silently
 * stopped them arriving on the phone.
 */
import { arrayRemove, arrayUnion, doc, setDoc } from 'firebase/firestore';
import { deleteToken, getMessaging, getToken } from 'firebase/messaging';

import { firestore } from '../firebase';

const VAPID_KEY =
  'BOIa9HTf1Ogw8b_-CR4pC-eD130g78RRXZy0nn98tcT2IrearEL-UU2c91cwm43-EZ86ueWg75HelHlK0dB-9M0';

/** The last token this browser is known to have registered, so a silent rotation can be reconciled. */
const LAST_TOKEN_STORAGE_KEY = 'pun_game_fcm_token';

/** The browser refused, so the app cannot do anything until the user relents. */
export class PermissionDeniedError extends Error {
  constructor() {
    super('Notification permission was not granted.');
    this.name = 'PermissionDeniedError';
  }
}

/** True once this browser has been granted permission. */
export function hasPermission(): boolean {
  return typeof Notification !== 'undefined' && Notification.permission === 'granted';
}

/**
 * iOS only delivers push to a site added to the home screen, so the permission
 * prompt on a normal Safari tab either never appears or leads nowhere.
 */
export function isIOSBrowser(): boolean {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !(window as { MSStream?: unknown }).MSStream
  );
}

/** Ask for permission if needed, then record this device's token. */
export async function registerDevice(userId: string): Promise<void> {
  if (!hasPermission()) {
    if ((await Notification.requestPermission()) !== 'granted') {
      throw new PermissionDeniedError();
    }
  }

  const token = await getToken(getMessaging(), { vapidKey: VAPID_KEY });
  if (!token) throw new Error('The browser did not return a notification token.');

  await setDoc(
    doc(firestore, 'userDevices', userId),
    { tokens: arrayUnion(token) },
    { merge: true },
  );
  window.localStorage.setItem(LAST_TOKEN_STORAGE_KEY, token);
}

/**
 * Reconcile this browser's token with what is stored, without prompting for
 * permission.
 *
 * `getToken` can silently hand back a new token — after a browser update, a
 * service worker update, or just periodic rotation — while the old one goes
 * on "succeeding" at FCM for some overlap window before it finally errors.
 * `registerDevice` only runs when a user flips a toggle, so a rotation that
 * happens in between was never reconciled, and the account ended up holding
 * both tokens: the same device getting sent every notification twice. Calling
 * this on every app load closes that gap.
 *
 * A no-op for anyone who has never granted permission.
 */
export async function reconcileDeviceToken(userId: string): Promise<void> {
  if (!hasPermission()) return;

  let token: string;
  try {
    token = await getToken(getMessaging(), { vapidKey: VAPID_KEY });
  } catch {
    return;
  }
  if (!token) return;

  const previous = window.localStorage.getItem(LAST_TOKEN_STORAGE_KEY);
  if (previous === token) return;

  const ref = doc(firestore, 'userDevices', userId);
  if (previous) {
    await setDoc(ref, { tokens: arrayRemove(previous) }, { merge: true });
  }
  await setDoc(ref, { tokens: arrayUnion(token) }, { merge: true });
  window.localStorage.setItem(LAST_TOKEN_STORAGE_KEY, token);
}

/**
 * Forget this device.
 *
 * Only ever removes the token belonging to *this* browser, so turning
 * notifications off on a laptop leaves the phone working.
 */
export async function unregisterDevice(userId: string): Promise<void> {
  let token: string | null = null;

  try {
    token = await getToken(getMessaging(), { vapidKey: VAPID_KEY });
  } catch {
    // No token to find, which is the state we are trying to reach anyway.
  }

  // Also remove whatever this browser last registered, in case it has since
  // rotated and getToken() above returned something different.
  const stored = window.localStorage.getItem(LAST_TOKEN_STORAGE_KEY);
  const stale = [token, stored].filter((t): t is string => Boolean(t));

  if (stale.length > 0) {
    await setDoc(
      doc(firestore, 'userDevices', userId),
      { tokens: arrayRemove(...stale) },
      { merge: true },
    );
  }
  window.localStorage.removeItem(LAST_TOKEN_STORAGE_KEY);

  try {
    await deleteToken(getMessaging());
  } catch (error) {
    // A token that will not delete still stops mattering once the server has
    // dropped it, and the server prunes anything FCM rejects.
    console.warn('[notifications] could not delete the FCM token:', error);
  }
}
